// The floor-plan editor's pure geometry layer (#201).
//
// Everything here is a plain function over plain data — no Angular, no DOM, no component state — so the maths that
// makes the infinite canvas behave (view transforms, cursor-anchored zoom, snapping, the shoelace centroid, live
// table→area matching, the origin re-base) is unit-testable without a TestBed and cannot drift into the template.
//
// **Units.** One floor-plan unit is **one centimetre** (the handoff's §1). Every `x`/`y`/`width`/`height` in this
// module — and in the stored `Table`/`Area`/`FloorPlanBackground` — is in that space; only the readout and the scale
// bar convert to metres for display. The screen relation is `screen = translate + world * scale`, so `scale` is
// "screen pixels per centimetre" and a `scale` of 1 draws 1 cm as 1 px.
//
// **Corner-anchored, not centre-anchored.** The reference implementation models a table by its centre (`wx`/`wy`);
// the stored contract (and the MAUI register that also reads it) models it by its **top-left corner**. Rather than
// convert on every gesture, the port works in the stored top-left space throughout: it is the same arithmetic (the
// reference's own resize keeps the top-left fixed too) and it keeps the canvas, the number inputs in the inspectors
// and the wire model talking about one coordinate.

// Types only. `table-layout.model.ts` imports runtime helpers from *this* module (its `normalizeArea` needs the
// shoelace centroid), so pulling values back the other way would close a runtime import cycle. `import type` is
// erased at compile time, which keeps the dependency one-directional where it matters.
import type {AreaNode, FloorArea, FloorTable} from './table-layout.model';

/**
 * The canvas view: `translate(x, y) scale(scale)` in CSS terms. Screen position of a world point is
 * `translate + world * scale`, so panning changes `x`/`y` and zooming changes `scale` plus whichever translation
 * keeps the anchor point still.
 */
export interface ViewTransform {
  /** Horizontal screen offset, in pixels relative to the viewport's top-left. */
  x: number;
  /** Vertical screen offset, in pixels relative to the viewport's top-left. */
  y: number;
  /** Screen pixels per floor-plan centimetre. */
  scale: number;
}

/** A point in screen space, relative to the viewport's top-left corner. */
export interface ScreenPoint {
  x: number;
  y: number;
}

/** An axis-aligned world-space box: `[minX, minY, maxX, maxY]`. */
export type Bounds = readonly [number, number, number, number];

/** Smallest zoom the *editor* allows (20 %, per the handoff's §2). */
export const MIN_SCALE = 0.2;

/** Largest zoom the *editor* allows (300 %, per the handoff's §2). */
export const MAX_SCALE = 3;

/**
 * The zoom range a *read-only* canvas allows — the Offene-Tische display mode's own 30 %–240 % (#205, its
 * handover's §5), deliberately narrower than the editor's.
 *
 * The editor needs to zoom out far enough to see a whole venue while dragging a table across it, and in far enough
 * to place a vertex on a centimetre. A live service view needs neither: below 30 % a marker's number is unreadable,
 * and above 240 % the room stops being a map.
 */
export const DISPLAY_MIN_SCALE = 0.3;

/** Largest zoom a read-only canvas allows — see {@link DISPLAY_MIN_SCALE}. */
export const DISPLAY_MAX_SCALE = 2.4;

/** One canvas's allowed zoom range. */
export interface ScaleRange {
  min: number;
  max: number;
}

/** The editor's range, and the default of every function below. */
export const EDITOR_SCALE_RANGE: ScaleRange = {min: MIN_SCALE, max: MAX_SCALE};

/** The read-only display mode's range. */
export const DISPLAY_SCALE_RANGE: ScaleRange = {min: DISPLAY_MIN_SCALE, max: DISPLAY_MAX_SCALE};

/**
 * Wheel sensitivity. `scale * exp(-deltaY * WHEEL_SENSITIVITY)` — exponential rather than linear so a notch feels
 * the same at every zoom level, and so the clamp is approached asymptotically instead of with a jolt.
 */
export const WHEEL_SENSITIVITY = 0.0016;

/** Zoom factor of one press of the `+` button; `−` applies its reciprocal-ish counterpart {@link ZOOM_OUT_FACTOR}. */
export const ZOOM_IN_FACTOR = 1.25;

/** Zoom factor of one press of the `−` button. */
export const ZOOM_OUT_FACTOR = 0.8;

/** Padding, in screen pixels, left around the content by {@link fitAllView} so nothing touches the edge. */
export const FIT_PADDING = 60;

/**
 * How close two pointer-downs on the *same* vertex must be, in milliseconds, to count as a double-tap (and remove
 * the vertex). Timed by hand rather than read off a native `dblclick`, because the handles are re-created on every
 * re-render — the browser only fires `dblclick` when both clicks land on the *same element instance*, which a
 * dragged, continuously re-rendered handle is not.
 */
export const DOUBLE_TAP_MS = 340;

/** How close, in screen pixels, a click must land to the draft's first vertex to close the polygon. */
export const CLOSE_DRAFT_PX = 16;

/**
 * Clamps a zoom level into the allowed range.
 *
 * @param range The canvas's own range; defaults to {@link EDITOR_SCALE_RANGE} so every existing caller (and its
 *   tests) keeps the editor's 20 %–300 % without passing anything.
 */
export function clampScale(scale: number, range: ScaleRange = EDITOR_SCALE_RANGE): number {
  return Math.min(range.max, Math.max(range.min, scale));
}

/** Screen position (viewport-relative pixels) of a world point under a view. */
export function toScreen(view: ViewTransform, worldX: number, worldY: number): ScreenPoint {
  return {x: view.x + worldX * view.scale, y: view.y + worldY * view.scale};
}

/** World position (centimetres) of a screen point (viewport-relative pixels) under a view. */
export function toWorld(view: ViewTransform, screenX: number, screenY: number): AreaNode {
  return {x: (screenX - view.x) / view.scale, y: (screenY - view.y) / view.scale};
}

/**
 * Zooms around a fixed screen point — the cursor-anchored wheel zoom. The world point currently under
 * `anchor` stays under it, which is what makes wheeling feel like moving a map rather than a slider.
 *
 * @param view The current view.
 * @param factor Multiplier to apply to the scale (clamped afterwards).
 * @param anchor The screen point to hold still.
 * @param range The canvas's allowed zoom range (see {@link clampScale}).
 */
export function zoomAround(
  view: ViewTransform, factor: number, anchor: ScreenPoint, range: ScaleRange = EDITOR_SCALE_RANGE
): ViewTransform {
  const scale = clampScale(view.scale * factor, range);
  const world = toWorld(view, anchor.x, anchor.y);
  return {scale, x: anchor.x - world.x * scale, y: anchor.y - world.y * scale};
}

/** The wheel gesture's zoom factor for a `wheel` event's `deltaY` (up/away = zoom in). */
export function wheelFactor(deltaY: number): number {
  return Math.exp(-deltaY * WHEEL_SENSITIVITY);
}

/** Zooms around the middle of the viewport — what the `+`/`−` buttons do. */
export function zoomCentered(
  view: ViewTransform, factor: number, width: number, height: number, range: ScaleRange = EDITOR_SCALE_RANGE
): ViewTransform {
  return zoomAround(view, factor, {x: width / 2, y: height / 2}, range);
}

/**
 * The view that puts the origin `(0, 0)` in the middle of the viewport at 100 % zoom. Moves the *camera* only —
 * no geometry is touched (that is {@link rebaseOrigin}'s job).
 */
export function centerOriginView(width: number, height: number): ViewTransform {
  return {scale: 1, x: width / 2, y: height / 2};
}

/** The world-space bounding box of everything placed, or `null` when the plan is empty. */
export function contentBounds(
  tables: readonly FloorTable[],
  areas: readonly FloorArea[],
  background: {x: number; y: number; width: number; height: number} | null
): Bounds | null {
  const boxes: Bounds[] = [];
  for (const table of tables) boxes.push([table.x, table.y, table.x + table.width, table.y + table.height]);
  for (const area of areas) for (const node of area.nodes) boxes.push([node.x, node.y, node.x, node.y]);
  if (background && background.width > 0 && background.height > 0) {
    boxes.push([background.x, background.y, background.x + background.width, background.y + background.height]);
  }
  if (boxes.length === 0) return null;

  return [
    Math.min(...boxes.map(b => b[0])),
    Math.min(...boxes.map(b => b[1])),
    Math.max(...boxes.map(b => b[2])),
    Math.max(...boxes.map(b => b[3]))
  ];
}

/**
 * The view that frames `bounds` in a `width × height` viewport with {@link FIT_PADDING} to spare — the fit-all
 * button. A degenerate (zero-extent) box divides by 1 instead of 0, so framing a single point still produces a
 * usable view rather than `Infinity`.
 */
export function fitBoundsView(
  bounds: Bounds, width: number, height: number, range: ScaleRange = EDITOR_SCALE_RANGE
): ViewTransform {
  const [minX, minY, maxX, maxY] = bounds;
  const scale = clampScale(Math.min(
    (width - FIT_PADDING * 2) / Math.max(1, maxX - minX),
    (height - FIT_PADDING * 2) / Math.max(1, maxY - minY)
  ), range);
  const centreX = (minX + maxX) / 2;
  const centreY = (minY + maxY) / 2;
  return {scale, x: width / 2 - centreX * scale, y: height / 2 - centreY * scale};
}

/**
 * The fit-all view for a whole plan, falling back to {@link centerOriginView} when there is nothing to frame.
 */
export function fitAllView(
  tables: readonly FloorTable[],
  areas: readonly FloorArea[],
  background: {x: number; y: number; width: number; height: number} | null,
  width: number,
  height: number,
  range: ScaleRange = EDITOR_SCALE_RANGE
): ViewTransform {
  const bounds = contentBounds(tables, areas, background);
  return bounds ? fitBoundsView(bounds, width, height, range) : centerOriginView(width, height);
}

/**
 * Rounds a coordinate onto the grid — or leaves it alone when snapping is off.
 *
 * Deliberately **unclamped**, unlike the fixed-viewport editor this replaced: the canvas is infinite and the origin
 * is movable, so every coordinate is legitimately signed and clamping to `[0, max]` would silently pin objects to
 * a border that no longer exists.
 *
 * @param value The raw coordinate.
 * @param grid The minor grid spacing in centimetres.
 * @param enabled Whether the snap toggle is on.
 */
export function snapTo(value: number, grid: number, enabled: boolean): number {
  if (!enabled || grid <= 0) return Math.round(value);
  return Math.round(value / grid) * grid;
}

/**
 * The **area-weighted centroid** (shoelace formula) of a polygon — the mirror of the backend's
 * `API.Core.Models.Booking.Entities.Area.AreaCentroid.Compute`, down to its degenerate-case fallbacks and its
 * round-half-away-from-zero, so a live preview of an area still being dragged lands exactly where the server will
 * persist its `CenterX`/`CenterY` on the next save.
 *
 * @param nodes The ordered outline; implicitly closed, so the first vertex must NOT be repeated at the end.
 * @returns The centroid rounded to whole centimetres, or `(0, 0)` for an empty outline.
 */
export function polygonCentroid(nodes: readonly AreaNode[]): AreaNode {
  if (nodes.length === 0) return {x: 0, y: 0};
  // A point or a line encloses no area, so its "middle" IS the vertex average — also the mid-draw state.
  if (nodes.length < 3) return vertexAverage(nodes);

  let doubleArea = 0;
  let sumX = 0;
  let sumY = 0;

  for (let i = 0; i < nodes.length; i++) {
    const current = nodes[i];
    const next = nodes[(i + 1) % nodes.length];
    const cross = current.x * next.y - next.x * current.y;
    doubleArea += cross;
    sumX += (current.x + next.x) * cross;
    sumY += (current.y + next.y) * cross;
  }

  // The centroid divides by 6A, i.e. by 3 * (2A). Collinear/duplicated vertices make that zero — never divide.
  const denominator = 3 * doubleArea;
  if (denominator === 0) return vertexAverage(nodes);

  return {x: roundAwayFromZero(sumX / denominator), y: roundAwayFromZero(sumY / denominator)};
}

/** The degenerate-case fallback: the plain arithmetic mean of every vertex. */
function vertexAverage(nodes: readonly AreaNode[]): AreaNode {
  const sum = nodes.reduce((acc, node) => ({x: acc.x + node.x, y: acc.y + node.y}), {x: 0, y: 0});
  return {x: roundAwayFromZero(sum.x / nodes.length), y: roundAwayFromZero(sum.y / nodes.length)};
}

/**
 * Rounds half **away from zero**, matching the backend's `MidpointRounding.AwayFromZero`: a centroid landing on
 * `x.5` must not flip with the polygon's winding direction.
 */
function roundAwayFromZero(value: number): number {
  return Math.sign(value) * Math.round(Math.abs(value));
}

/**
 * How close to the true optimum {@link polygonLabelAnchor} refines before it stops, in centimetres. The result is
 * rounded to a whole centimetre anyway, so refining below one unit cannot change the answer.
 *
 * Mirrors the backend's `AreaLabelAnchor.Precision`.
 */
export const LABEL_ANCHOR_PRECISION = 1;

/** Guard ceiling on subdivision steps, mirroring the backend's `AreaLabelAnchor.MaxIterations`. */
export const LABEL_ANCHOR_MAX_ITERATIONS = 10000;

/**
 * The polygon's **pole of inaccessibility** — the interior point furthest from any edge, i.e. the centre of the
 * largest circle that fits inside the outline. This is where an area's **name label** is drawn.
 *
 * **Why not the centroid.** {@link polygonCentroid} answers "where is the mass?", which is right for the
 * middle-point *marker* but wrong for a *label*: the area-weighted centroid of a non-convex polygon is not
 * guaranteed to lie inside the polygon at all. A U-shaped bar counter's centroid sits in the notch between its
 * arms — empty canvas — so the caption floats outside the region it names. This function's answer is interior by
 * construction. (The bounding-box corner this editor previously anchored captions at fails the same way: the
 * corner of an L/U/T-shaped room's bounding box is usually a spot the room does not occupy.)
 *
 * **The algorithm** is the grid-refinement search popularised by Mapbox's `polylabel`, and is the mirror of the
 * backend's `API.Core.Models.Booking.Entities.Area.AreaLabelAnchor.Compute` — same precision, same degenerate
 * fallbacks, same round-half-away-from-zero — so a live preview of an area still being dragged lands where the
 * server will persist its `LabelX`/`LabelY` on the next save. The bounding box is covered in square cells scored
 * by the signed distance from their centre to the boundary; cells are visited best-upper-bound-first and only
 * subdivided when their bound still beats the incumbent, which is what keeps the search cheap.
 *
 * @param nodes The ordered outline; implicitly closed, so the first vertex must NOT be repeated at the end.
 * @returns The anchor rounded to whole centimetres, or `(0, 0)` for an empty outline.
 */
export function polygonLabelAnchor(nodes: readonly AreaNode[]): AreaNode {
  if (nodes.length === 0) return {x: 0, y: 0};
  // No enclosed interior ⇒ nothing to inscribe a circle in; the centroid IS the answer (also the mid-draw state).
  if (nodes.length < 3) return polygonCentroid(nodes);

  let minX = nodes[0].x, minY = nodes[0].y, maxX = nodes[0].x, maxY = nodes[0].y;
  for (const node of nodes) {
    if (node.x < minX) minX = node.x;
    if (node.x > maxX) maxX = node.x;
    if (node.y < minY) minY = node.y;
    if (node.y > maxY) maxY = node.y;
  }

  const width = maxX - minX;
  const height = maxY - minY;
  // A zero-extent box (identical or axis-collinear vertices) has no interior to search.
  if (width <= 0 || height <= 0) return polygonCentroid(nodes);

  // Seed cell size: the smaller extent, so the first pass is at least one cell deep in the narrow direction.
  const cellSize = Math.min(width, height);
  const half = cellSize / 2;

  const queue = new MaxHeap();
  const makeCell = (x: number, y: number, h: number): LabelCell => {
    const distance = signedDistanceToPolygon(nodes, x, y);
    // The furthest a point in a square can be from its centre is the half-diagonal, and the signed distance is
    // 1-Lipschitz — so nothing in this cell can score better than `distance + h * √2`. That bound licenses the
    // prune below.
    return {x, y, h, distance, bound: distance + h * Math.SQRT2};
  };

  for (let x = minX; x < maxX; x += cellSize) {
    for (let y = minY; y < maxY; y += cellSize) {
      queue.push(makeCell(x + half, y + half, half));
    }
  }

  // The centroid is a strong opening guess for the convex majority of areas, so seeding the incumbent with it
  // usually prunes most of the grid on the first pops. A centroid outside the polygon scores negative and is
  // beaten by the first genuinely interior cell, so it is only ever a seed.
  const centroid = polygonCentroid(nodes);
  let best = makeCell(centroid.x, centroid.y, 0);
  const boxCentre = makeCell(minX + width / 2, minY + height / 2, 0);
  if (boxCentre.distance > best.distance) best = boxCentre;

  let iterations = 0;
  while (queue.size > 0 && iterations < LABEL_ANCHOR_MAX_ITERATIONS) {
    const cell = queue.pop()!;
    if (cell.distance > best.distance) best = cell;

    // Nothing in this cell can beat the incumbent, so the whole subtree below it is discarded.
    if (cell.bound - best.distance <= LABEL_ANCHOR_PRECISION) continue;

    iterations++;
    const quarter = cell.h / 2;
    queue.push(makeCell(cell.x - quarter, cell.y - quarter, quarter));
    queue.push(makeCell(cell.x + quarter, cell.y - quarter, quarter));
    queue.push(makeCell(cell.x - quarter, cell.y + quarter, quarter));
    queue.push(makeCell(cell.x + quarter, cell.y + quarter, quarter));
  }

  return {x: roundAwayFromZero(best.x), y: roundAwayFromZero(best.y)};
}

/** One candidate square in {@link polygonLabelAnchor}'s search. */
interface LabelCell {
  /** Cell centre. */
  x: number;
  y: number;
  /** Half the cell's edge length. */
  h: number;
  /** Signed distance from the centre to the polygon boundary — positive inside. */
  distance: number;
  /** The best score any point in this cell could have; the heap's ordering key. */
  bound: number;
}

/**
 * A binary max-heap keyed on {@link LabelCell.bound}, hand-rolled because the platform has no priority queue and
 * re-sorting an array on every push would turn the search quadratic.
 *
 * Ordering on the *bound* rather than the distance is what makes the search a branch-and-bound (and therefore
 * globally optimal) rather than a greedy descent into the first local maximum it finds.
 */
class MaxHeap {
  private readonly items: LabelCell[] = [];

  get size(): number {
    return this.items.length;
  }

  push(cell: LabelCell): void {
    this.items.push(cell);
    let index = this.items.length - 1;
    while (index > 0) {
      const parent = (index - 1) >> 1;
      if (this.items[parent].bound >= this.items[index].bound) break;
      [this.items[parent], this.items[index]] = [this.items[index], this.items[parent]];
      index = parent;
    }
  }

  pop(): LabelCell | undefined {
    if (this.items.length === 0) return undefined;
    const top = this.items[0];
    const last = this.items.pop()!;
    if (this.items.length === 0) return top;

    this.items[0] = last;
    let index = 0;
    for (;;) {
      const left = index * 2 + 1;
      const right = left + 1;
      let largest = index;
      if (left < this.items.length && this.items[left].bound > this.items[largest].bound) largest = left;
      if (right < this.items.length && this.items[right].bound > this.items[largest].bound) largest = right;
      if (largest === index) break;
      [this.items[largest], this.items[index]] = [this.items[index], this.items[largest]];
      index = largest;
    }
    return top;
  }
}

/**
 * The shortest distance from a point to the polygon's **boundary** — the minimum over every closing edge, treated
 * as a finite segment rather than an infinite line.
 *
 * Segments, not lines: the nearest point on an edge is frequently one of its endpoints (exactly what happens at a
 * concave notch), and an infinite-line distance would report a smaller value than the shape actually allows.
 */
export function distanceToPolygonBoundary(nodes: readonly AreaNode[], x: number, y: number): number {
  if (nodes.length === 0) return 0;
  let min = Number.POSITIVE_INFINITY;
  for (let i = 0, j = nodes.length - 1; i < nodes.length; j = i++) {
    const squared = squaredDistanceToSegment(x, y, nodes[j], nodes[i]);
    if (squared < min) min = squared;
  }
  // One square root at the end: comparing squares is order-preserving.
  return Math.sqrt(min);
}

/**
 * The **signed** distance to the boundary: positive inside the polygon, negative outside. The sign is what makes
 * {@link polygonLabelAnchor} return an interior point by construction — every outside candidate scores below
 * every inside one.
 */
export function signedDistanceToPolygon(nodes: readonly AreaNode[], x: number, y: number): number {
  const distance = distanceToPolygonBoundary(nodes, x, y);
  return isInsidePolygon(nodes, x, y) ? distance : -distance;
}

/** Squared distance from a point to the finite segment `a`→`b`, so callers can compare without a square root. */
function squaredDistanceToSegment(x: number, y: number, a: AreaNode, b: AreaNode): number {
  let ax = a.x;
  let ay = a.y;
  const dx = b.x - ax;
  const dy = b.y - ay;

  // A zero-length edge (duplicate consecutive vertices) degenerates to a point — projecting would divide by zero.
  if (dx !== 0 || dy !== 0) {
    // Projection parameter onto the segment's infinite line, clamped to [0, 1] so the foot stays on the SEGMENT.
    const t = ((x - ax) * dx + (y - ay) * dy) / (dx * dx + dy * dy);
    if (t > 1) {
      ax = b.x;
      ay = b.y;
    } else if (t > 0) {
      ax += dx * t;
      ay += dy * t;
    }
  }

  const offsetX = x - ax;
  const offsetY = y - ay;
  return offsetX * offsetX + offsetY * offsetY;
}

/**
 * Whether a point lies inside a polygon, by ray casting (the crossing-number test). A vertex/edge exactly on the
 * ray is resolved by the strict `>` / non-strict comparison pair, so a point on a shared edge lands in exactly one
 * of two adjacent polygons rather than both or neither.
 */
export function isInsidePolygon(nodes: readonly AreaNode[], x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = nodes.length - 1; i < nodes.length; j = i++) {
    const a = nodes[i];
    const b = nodes[j];
    if (a.y > y !== b.y > y && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/**
 * The area a point falls into — the live table→area match of the handoff's §6.
 *
 * **Last one wins on overlap.** The list is scanned backwards, so where two areas overlap the one drawn *later*
 * (the one on top, and the more recently drawn) claims the table. Deterministic and matches what the admin sees.
 *
 * @returns The matching area, or `null` when the point is in no area at all.
 */
export function areaAt(areas: readonly FloorArea[], x: number, y: number): FloorArea | null {
  for (let i = areas.length - 1; i >= 0; i--) {
    if (isInsidePolygon(areas[i].nodes, x, y)) return areas[i];
  }
  return null;
}

/** The centre point of a table's footprint — what {@link areaAt} matches on. */
export function tableCenter(table: Pick<FloorTable, 'x' | 'y' | 'width' | 'height'>): AreaNode {
  return {x: table.x + table.width / 2, y: table.y + table.height / 2};
}

/** The area a table's centre falls into. */
export function areaOfTable(areas: readonly FloorArea[], table: FloorTable): FloorArea | null {
  const centre = tableCenter(table);
  return areaAt(areas, centre.x, centre.y);
}

/**
 * Re-bases every coordinate so that `origin` becomes the new `(0, 0)`, and compensates the view so **nothing moves
 * on screen** — only the numbers change (the handoff's §7).
 *
 * The compensation is the whole point: subtracting the origin from the geometry would visually shift the plan by
 * `origin * scale` pixels, so the same amount is added back to the view's translation.
 *
 * @param origin The world point that should become the origin.
 * @param plan The current geometry.
 * @param view The current view, compensated in the result.
 */
export function rebaseOrigin<TBackground extends {x: number; y: number}>(
  origin: AreaNode,
  plan: {
    tables: readonly FloorTable[];
    areas: readonly FloorArea[];
    background: TBackground | null;
  },
  view: ViewTransform
): {tables: FloorTable[]; areas: FloorArea[]; background: TBackground | null; view: ViewTransform} {
  return {
    tables: plan.tables.map(table => ({...table, x: table.x - origin.x, y: table.y - origin.y})),
    areas: plan.areas.map(area => ({
      ...area,
      nodes: area.nodes.map(node => ({x: node.x - origin.x, y: node.y - origin.y})),
      // The server recomputes both persisted points from the nodes on save; shift the local copies so neither the
      // marker nor the caption jumps for the one frame between the re-base and the round-trip. A re-base is a
      // pure translation, so shifting is exact — the anchor of a translated polygon is the translated anchor.
      centerX: area.centerX - origin.x,
      centerY: area.centerY - origin.y,
      labelX: area.labelX - origin.x,
      labelY: area.labelY - origin.y
    })),
    background: plan.background
      ? {...plan.background, x: plan.background.x - origin.x, y: plan.background.y - origin.y}
      : null,
    view: {...view, x: view.x + origin.x * view.scale, y: view.y + origin.y * view.scale}
  };
}

/** The round lengths the scale bar is allowed to show, in centimetres — 10 cm to 10 m. */
export const SCALE_BAR_STEPS = [10, 25, 50, 100, 200, 500, 1000, 2000, 5000, 10000] as const;

/** The widest the scale bar may get, in screen pixels. */
const SCALE_BAR_MAX_PX = 150;

/** Below this screen width the chosen length is too short to read, so the smallest step is used instead. */
const SCALE_BAR_MIN_PX = 40;

/**
 * Picks the scale bar's round length for the current zoom: the **largest** step that still fits in
 * {@link SCALE_BAR_MAX_PX}, falling back to the smallest step when even that would render shorter than
 * {@link SCALE_BAR_MIN_PX} (which happens when zoomed far out).
 *
 * @param scale The current zoom (screen pixels per centimetre).
 * @returns The length in centimetres, its rendered pixel width, and its display label.
 */
export function scaleBarFor(scale: number): {centimetres: number; pixels: number; label: string} {
  let centimetres: number = SCALE_BAR_STEPS[0];
  for (const step of SCALE_BAR_STEPS) {
    if (step * scale <= SCALE_BAR_MAX_PX) centimetres = step;
  }
  if (centimetres * scale < SCALE_BAR_MIN_PX) centimetres = SCALE_BAR_STEPS[0];

  return {
    centimetres,
    pixels: Math.round(centimetres * scale),
    label: centimetres >= 100 ? `${centimetres / 100} m` : `${centimetres} cm`
  };
}

/** Formats a centimetre coordinate for the metre readout (the handoff's §1). */
export function formatMetres(centimetres: number): string {
  return (centimetres / 100).toFixed(2);
}

/**
 * Turns a `#RRGGBB` colour into an `rgba(…)` string at the given alpha — how an area's own colour becomes its 40 %
 * fill and its 55 % centroid cross. An unparseable value degrades to the input rather than emitting `rgba(NaN…)`.
 */
export function withAlpha(hex: string, alpha: number): string {
  const match = /^#([0-9a-f]{6})$/i.exec((hex ?? '').trim());
  if (!match) return hex;
  const value = Number.parseInt(match[1], 16);
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
}

/**
 * Multiplies a `#RRGGBB` colour's channels by `factor` — how an area's own colour becomes the darker shade its
 * name label is drawn in.
 *
 * Deliberately a *value* rather than a CSS `filter: brightness(…)`: a filter is invisible to contrast tooling
 * (axe measures the authored colour, not the composited one), so a label that renders perfectly legibly still
 * reports as a failure. Computing the shade here makes what is measured what is rendered.
 *
 * An unparseable value degrades to the input rather than emitting `rgb(NaN…)`.
 */
export function darken(hex: string, factor: number): string {
  const match = /^#([0-9a-f]{6})$/i.exec((hex ?? '').trim());
  if (!match) return hex;
  const value = Number.parseInt(match[1], 16);
  const channel = (shift: number) =>
    Math.max(0, Math.min(255, Math.round(((value >> shift) & 255) * factor)));
  return `rgb(${channel(16)}, ${channel(8)}, ${channel(0)})`;
}

/**
 * The rendered height of the background image: **always** derived from its width and the decoded image's aspect
 * ratio, never stored (the handoff's §8, and the backend deliberately has no `Height` column). That is what makes
 * the resize handle aspect-locked by construction — there is no pair of numbers that can drift out of proportion.
 *
 * @param width The rendered width in centimetres.
 * @param naturalWidth The decoded image's intrinsic width in pixels.
 * @param naturalHeight The decoded image's intrinsic height in pixels.
 * @returns The rendered height in centimetres; `0` when the aspect ratio is unknowable.
 */
export function backgroundHeight(width: number, naturalWidth: number, naturalHeight: number): number {
  if (!(naturalWidth > 0) || !(naturalHeight > 0)) return 0;
  return width * (naturalHeight / naturalWidth);
}

/**
 * The width a freshly uploaded image is placed at when the server reports `width === 0` ("not placed yet"): its own
 * natural width, so a plan exported at 1 px per cm lands life-size, but capped so a 6000 px scan does not fill the
 * viewport at 60 m across.
 */
export const BACKGROUND_MAX_INITIAL_WIDTH = 2000;

/** Smallest width the background may be resized to — below this the frame is unusable. */
export const MIN_BACKGROUND_WIDTH = 40;

/** The width to render a background at, resolving the "not placed yet" (`width === 0`) case. */
export function backgroundWidthOrNatural(storedWidth: number, naturalWidth: number): number {
  if (storedWidth > 0) return storedWidth;
  if (!(naturalWidth > 0)) return 0;
  return Math.min(naturalWidth, BACKGROUND_MAX_INITIAL_WIDTH);
}

/**
 * Whether a pointer-down on a vertex completes a double-tap (and therefore removes it) — see {@link DOUBLE_TAP_MS}
 * for why this is timed by hand instead of using `dblclick`.
 *
 * @param previous The previous tap on any vertex, or `null` when there was none.
 * @param index The vertex index just pressed.
 * @param now The current timestamp in milliseconds.
 */
export function isDoubleTap(previous: {index: number; at: number} | null, index: number, now: number): boolean {
  return previous !== null && previous.index === index && now - previous.at < DOUBLE_TAP_MS;
}
